// Substitutos de window.confirm e window.prompt no estilo do app.
//
// Por que: o navegador desenha esses diálogos com chrome do SO, fora do
// padrão visual, sem dark mode, sem markdown e sem possibilidade de animação
// de origem/destino. Aqui temos um modal único, controlado por signals e
// invocado de forma imperativa (Promise) — quem chama escreve quase o mesmo
// código de antes:
//
//   const ok = await openConfirm({ title, message });
//   const v  = await openPrompt({ title, message, defaultValue });
//
// Mount UMA vez em <App />: <ConfirmRoot />  e  <PromptRoot />.

import { signal } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import { AlertTriangle, Loader2, X } from "lucide-react";

// =====================
// Confirm
// =====================
interface ConfirmRequest {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  resolve: (ok: boolean) => void;
}
const confirmReq = signal<ConfirmRequest | null>(null);

export function openConfirm(opts: {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}): Promise<boolean> {
  return new Promise((resolve) => {
    // Se já houver um modal aberto, fecha como cancelado primeiro.
    if (confirmReq.value) confirmReq.value.resolve(false);
    confirmReq.value = { ...opts, resolve };
  });
}

export function ConfirmRoot() {
  const req = confirmReq.value;
  if (!req) return null;
  function close(ok: boolean) {
    if (!req) return;
    req.resolve(ok);
    confirmReq.value = null;
  }
  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) close(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") close(false);
      }}
    >
      <div class="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-xl animate-in fade-in zoom-in-95">
        <div class="mb-3 flex items-start gap-3">
          {req.danger && (
            <span class="grid size-8 shrink-0 place-items-center rounded-md bg-red-500/15 text-red-600 dark:text-red-400">
              <AlertTriangle className="size-4" />
            </span>
          )}
          <div class="min-w-0 flex-1">
            <h2 id="confirm-title" class="text-base font-bold leading-snug">
              {req.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => close(false)}
            class="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            title="Fechar"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </div>
        <p class="whitespace-pre-line text-sm text-muted-foreground">{req.message}</p>
        <div class="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {req.cancelLabel !== "" && (
            <button
              type="button"
              onClick={() => close(false)}
              class="inline-flex h-10 items-center justify-center rounded-md border border-border bg-background px-4 text-sm font-semibold hover:bg-accent"
            >
              {req.cancelLabel ?? "Cancelar"}
            </button>
          )}
          <button
            type="button"
            onClick={() => close(true)}
            autoFocus
            class={`inline-flex h-10 items-center justify-center rounded-md px-4 text-sm font-bold text-white ${
              req.danger
                ? "bg-red-600 hover:brightness-110"
                : "bg-primary hover:brightness-110"
            }`}
          >
            {req.confirmLabel ?? "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Atalho pra alert() — modal só de OK, sem botão de cancelar. Útil pra
 *  exibir mensagens de erro de operações async sem cair no alert nativo. */
export function openAlert(opts: {
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
}): Promise<void> {
  return new Promise((resolve) => {
    if (confirmReq.value) confirmReq.value.resolve(false);
    confirmReq.value = {
      title: opts.title,
      message: opts.message,
      confirmLabel: opts.confirmLabel ?? "OK",
      cancelLabel: "",
      danger: opts.danger,
      resolve: () => resolve()
    };
  });
}

// =====================
// Prompt
// =====================
interface PromptRequest {
  title: string;
  message?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  multiline?: boolean;
  maxLength?: number;
  danger?: boolean;
  /** Texto exato que precisa ser digitado pra liberar o confirm (case-insensitive). */
  requirePhrase?: string;
  /** Validador adicional: retorne string com erro ou null se OK. */
  validate?: (v: string) => string | null;
  resolve: (value: string | null) => void;
}
const promptReq = signal<PromptRequest | null>(null);

export function openPrompt(opts: {
  title: string;
  message?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  multiline?: boolean;
  maxLength?: number;
  danger?: boolean;
  requirePhrase?: string;
  validate?: (v: string) => string | null;
}): Promise<string | null> {
  return new Promise((resolve) => {
    if (promptReq.value) promptReq.value.resolve(null);
    promptReq.value = { ...opts, resolve };
  });
}

export function PromptRoot() {
  const req = promptReq.value;
  if (!req) return null;
  return <PromptModalInner req={req} key={req.title + (req.defaultValue ?? "")} />;
}

function PromptModalInner({ req }: { req: PromptRequest }) {
  const [value, setValue] = useState<string>(req.defaultValue ?? "");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    const id = setTimeout(() => {
      inputRef.current?.focus();
      // Select pra single-line se houver default — facilita substituir.
      if (!req.multiline && inputRef.current && "select" in inputRef.current) {
        (inputRef.current as HTMLInputElement).select();
      }
    }, 30);
    return () => clearTimeout(id);
  }, []);

  function close(v: string | null) {
    req.resolve(v);
    promptReq.value = null;
  }

  function submit() {
    if (busy) return;
    const trimmed = value.trim();
    if (req.requirePhrase) {
      if (trimmed.toLowerCase() !== req.requirePhrase.toLowerCase()) {
        setErr(`Digite exatamente "${req.requirePhrase}" pra confirmar.`);
        return;
      }
    }
    if (req.validate) {
      const e = req.validate(trimmed);
      if (e) {
        setErr(e);
        return;
      }
    }
    setBusy(true);
    close(trimmed);
  }

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) close(null);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") close(null);
        else if (e.key === "Enter" && !req.multiline && !e.shiftKey) {
          e.preventDefault();
          submit();
        }
      }}
    >
      <div class="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-xl animate-in fade-in zoom-in-95">
        <div class="mb-3 flex items-start gap-3">
          {req.danger && (
            <span class="grid size-8 shrink-0 place-items-center rounded-md bg-red-500/15 text-red-600 dark:text-red-400">
              <AlertTriangle className="size-4" />
            </span>
          )}
          <div class="min-w-0 flex-1">
            <h2 class="text-base font-bold leading-snug">{req.title}</h2>
          </div>
          <button
            type="button"
            onClick={() => close(null)}
            class="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            title="Fechar"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </div>
        {req.message && (
          <p class="mb-3 whitespace-pre-line text-sm text-muted-foreground">
            {req.message}
          </p>
        )}
        {req.multiline ? (
          <textarea
            ref={inputRef as any}
            value={value}
            placeholder={req.placeholder}
            maxLength={req.maxLength}
            onInput={(e) => {
              setValue((e.target as HTMLTextAreaElement).value);
              if (err) setErr(null);
            }}
            class="w-full min-h-[8rem] resize-y rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            aria-label={req.title}
          />
        ) : (
          <input
            ref={inputRef as any}
            type="text"
            value={value}
            placeholder={req.placeholder}
            maxLength={req.maxLength}
            onInput={(e) => {
              setValue((e.target as HTMLInputElement).value);
              if (err) setErr(null);
            }}
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            aria-label={req.title}
          />
        )}
        {err && (
          <p class="mt-2 text-xs text-destructive" role="alert">
            {err}
          </p>
        )}
        <div class="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => close(null)}
            class="inline-flex h-10 items-center justify-center rounded-md border border-border bg-background px-4 text-sm font-semibold hover:bg-accent"
          >
            {req.cancelLabel ?? "Cancelar"}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            class={`inline-flex h-10 items-center justify-center gap-1.5 rounded-md px-4 text-sm font-bold text-white disabled:opacity-60 ${
              req.danger
                ? "bg-red-600 hover:brightness-110"
                : "bg-primary hover:brightness-110"
            }`}
          >
            {busy && <Loader2 className="size-3.5 animate-spin" />}
            {req.confirmLabel ?? "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
}
